import type { AwsRum } from "aws-rum-web"
import { useCallback, useContext } from "react"
import { CloudWatchRumContext } from "./CloudWatchRumProvider"

const useCloudWatch = () => {
  const client = useContext(CloudWatchRumContext)

  const recordPageView = useCallback(
    (...params: Parameters<AwsRum["recordPageView"]>) => {
      client?.recordPageView(...params)
    },
    [client],
  )

  /**
   * send error to AWS RUM and log in console
   */
  const logError = useCallback(
    (identifier: string, error: unknown, ...params: unknown[]) => {
      let message = identifier
      if (params && params.length > 0) {
        message = message.replace(/{(\d+)}/g, (_, index) => {
          return params[index] !== undefined
            ? String(params[index])
            : `{${index}}`
        })
      }
      console.error(message, error)
      client?.recordError(
        new Error(message, {
          cause: error,
        }),
      )
    },
    [client],
  )

  const logEvent = useCallback(
    (eventType: string, eventData: object) => {
      console.error(`${eventType}:`, JSON.stringify(eventData, null, 2))
      client?.recordEvent(eventType, eventData)
    },
    [client],
  )

  /**
   * Log a client-side event with auto-injected NZ timestamp and user agent.
   * Use for tracking user actions, navigation failures, validation errors etc.
   */
  const logClientEvent = useCallback(
    (eventType: string, eventData: Record<string, unknown>) => {
      const enrichedData = {
        ...eventData,
        timestamp: new Date().toLocaleString("en-NZ", {
          timeZone: "Pacific/Auckland",
        }),
        url: typeof window !== "undefined" ? window.location.pathname : "",
      }
      console.error(`${eventType}:`, JSON.stringify(enrichedData, null, 2))
      client?.recordEvent(eventType, enrichedData)
    },
    [client],
  )

  return {
    client,
    recordPageView,
    logError,
    logEvent,
    logClientEvent,
  }
}

export default useCloudWatch
